import { API_IDS } from '@intigral/constant';
import { IGatewayConfigurationResponse } from '../../entity/interface/iGatewayConfiguration';
import { IGatewayLanguageResponse } from '../../entity/interface/iGatewayLanguage';
import { IGatewayScreenResponse } from '../../entity/interface/iGatewayScreen';
import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { container } from '../../DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { IGatewayRepository } from '../../core/repositories/interfaces/iGatewayRepository';
import { IApiPathManager } from '../config/ApiPathManager';
import { JsonType } from '@intigral/types';
import { ScreenData } from '../../core/entities/ScreenEntities';

class GatewayRepository implements IGatewayRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async getGatewayConfiguration(
        endpoint: string,
        params: string,
        appKey: string
    ): Promise<IGatewayConfigurationResponse> {
        const additionalParams = params.split('&').reduce((acc: { [key: string]: any }, current) => {
            const [key, value] = current.split('=');
            acc[key] = value;
            return acc;
        }, {});

        const response = await this.http.get<IGatewayConfigurationResponse>(endpoint, {
            params: {
                appKey, // Pass the base key
                ...additionalParams, // Spread the additional parameters
            },
        });
        return response.data;
    }

    async getGatewayLanguage(appKey: string, langugae: string): Promise<JsonType> {
        // const path = `https://prod-cdn-droidbox-assets.intigral-ott.net/jawwy-staging-cms-config-released/stc-tv/locale/configs/wcp_app_${langugae}.json`;
        const path = this.apiPathManager.getPath(
            langugae === 'en' ? API_IDS.GATEWAY_LANGUAGE_EN : API_IDS.GATEWAY_LANGUAGE_AR
        );
        const response = await this.http.get<JsonType>(path, {
            withCredentials: false,
            params: {
                language: langugae,
                appKey,
            },
        });
        return response.data;
    }
    async getGatewayScreen(appKey: string, layout: string, screen: string, params: string): Promise<ScreenData> {
        const additionalParams = params.split('&').reduce((acc: { [key: string]: any }, current) => {
            const [key, value] = current.split('=');
            acc[key] = value;
            return acc;
        }, {});

        // Destructure 'platform' to prevent it from being spread into params and causing duplicate query param
        const { platform, ...restParams } = additionalParams;

        const response = await this.http.get<ScreenData>(this.apiPathManager.getPath(API_IDS.GATEWAY_SCREEN), {
            params: {
                layoutName: layout,
                screenName: screen,
                platformType: additionalParams['platform'] || 'browser',
                apiKey: appKey,
                ...restParams, // Spread the remaining parameters
            },
        });
        return response.data;
    }
}

export default GatewayRepository;
